// External libraries
import { Component, createRef } from "react";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { FieldWrapper } from "@progress/kendo-react-form";
import { Signature } from "@progress/kendo-react-inputs";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import { validators } from "../../Form/validators";
import { Invisible, validateStageStep } from "../../Utilities/EntryUtils";

// Signals
import { disabledSections, formErrors, template } from "../../Entry/Manage/EditEntrySignals";

export class SignatureField extends Component {
  inputRef = createRef();

  constructor({
    field,
    value,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    eventIndex,
    labelVisible,
    galleryParentName,
    isStageField,
    isPrintPreview,
  }) {
    super();
    const { fieldName, properties } = field;
    this.state = {
      value: value || "",
      valid: true,
      invisible: false,
      touched: false,
      disabled: false,
    };
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.handleFieldChange = handleFieldChange;
    this.galleryParentName = galleryParentName;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.isRequired = properties?.IsRequired;
    this.displayNameHorizontally = properties?.DisplayNameHorizontally;
    this.isStageField = isStageField;
    this.isPrintPreview = isPrintPreview;
    this.labelVisible = labelVisible;
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.label = properties?.Label || `${fieldName}:`;
    this.visibilityRules = properties?.VisibilityRules;
    this.disposeValidationEffect = () => {};
    this.disposeDependencyEffect = () => {};
    this.disposeClearFieldsEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  componentDidMount() {
    if (this.isRequired) {
      if (!this.validate(this.state.value, this.isRequired)) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      } else {
        this.setState({ valid: true });
      }
      this.handleValidationEffect();
    }

    if (this.visibilityRules) this.handleDependencyEffect();

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
  }

  componentWillUnmount() {
    this.disposeValidationEffect();
    this.disposeDependencyEffect();
    this.disposeClearFieldsEffect();
    this.disposeDisabledSectionsEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      this.state.value !== nextState.value ||
      this.state.valid !== nextState.valid ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled ||
      this.state.touched !== nextState.touched
    );
  }

  getSnapshotBeforeUpdate(_, prevState) {
    return {
      visibilityChange: prevState.invisible && !this.state.invisible,
      valueChange: prevState.value !== this.state.value,
    };
  }

  componentDidUpdate(_, __, snapshot) {
    if (snapshot.visibilityChange) this.setDefaultValue();
    if (this.state.invisible) this.clearField();
    if (!this.state.invisible && this.isRequired) {
      this.setState(
        (prevState) => ({ valid: this.validate(prevState.value, this.isRequired) }),
        () => {
          this.updateFormErrors();
        }
      );
    }
    if (this.state.disabled) this.clearFormErrors();
  }

  handleValidationEffect() {
    this.disposeValidationEffect = effect(() => {
      this.setState({
        valid: formErrors.value[this.sectionName]
          ? formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1
          : true,
      });
    });
  }

  validate(value, isRequired) {
    return isRequired ? validators.required(value) : true;
  }

  updateFormErrors() {
    if (!this.state.valid) {
      if (
        formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1 ||
        !formErrors.value[this.sectionName]
      ) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
    } else {
      this.clearFormErrors();
    }
  }

  clearFormErrors() {
    const newFormErrors = { ...formErrors.value };
    const sectionErrors = newFormErrors[this.sectionName];
    if (sectionErrors) {
      newFormErrors[this.sectionName] = sectionErrors.filter((error) => error !== this.fieldName);
    }
    formErrors.value = newFormErrors;
  }

  handleDependencyEffect() {
    this.disposeDependencyEffect = effect(() => {
      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      this.setState({
        invisible: result.invisible && !result.disabled,
        disabled: result.invisible && result.disabled,
      });
    });
  }

  clearField() {
    this.clearFormErrors();
    this.setState({ value: "" });
    this.handleFieldChange({
      e: { target: { name: this.fieldName }, value: "" },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  }

  setDefaultValue() {
    this.setState({
      value: "",
    });
    this.handleFieldChange({
      e: { target: { name: this.fieldName }, value: "" },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  }

  onChange = (e) => {
    this.setState(
      {
        valid: this.validate(e?.value, this.isRequired),
        value: e?.value,
        touched: true,
      },
      () => {
        this.updateFormErrors();
        this.handleFieldChange({
          e: { target: { name: this.fieldName }, value: e.value },
          sectionIndex: this.sectionIndex,
          fieldIndex: this.fieldIndex,
          eventIndex: this.eventIndex,
        });
        if (this.isStageField) validateStageStep();
      }
    );
  };

  render() {
    if (this.state.invisible) return null;
    const { value, valid, touched } = this.state;

    return (
      <Ripple>
        <div
          ref={this.inputRef}
          className="k-d-flex k-flex-col"
        >
          <FieldWrapper>
            <div
              className={this.displayNameHorizontally ? "k-d-flex k-align-items-center k-mt-5" : ""}
            >
              {/* Label to the left */}
              {this.displayNameHorizontally && this.labelVisible && (
                <Label
                  editorId={this.fieldName}
                  editorValid={this.isRequired ? this.state.valid : true}
                  style={{ marginRight: "8px", fontWeight: "bold" }}
                >
                  {this.label}
                </Label>
              )}

              {!this.displayNameHorizontally && this.labelVisible && (
                <Label
                  id={this.fieldName + "_label"}
                  className="signature-label"
                >
                  {this.label}
                </Label>
              )}

              <div
                className="k-form-field-wrap"
                style={{ flex: 1 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Signature
                    value={value}
                    onChange={this.onChange}
                    smooth={true}
                    maximizable={true}
                    hideLine={true}
                    color="#000000"
                    valid={this.isRequired ? this.state.valid : true}
                    readOnly={this.isPrintPreview}
                  />
                  <If
                    condition={
                      this.fieldInformation !== undefined &&
                      this.fieldInformation !== "" &&
                      this.fieldInformation !== "<p></p>"
                    }
                  >
                    <TooltipPopupIcon content={this.fieldInformation} />
                  </If>
                </div>
              </div>
            </div>
            <If condition={this.hint}>
              <Hint>{this.hint}</Hint>
            </If>
            <If condition={!valid && touched}>
              <Error>Error: This field is required.</Error>
            </If>
          </FieldWrapper>
        </div>
      </Ripple>
    );
  }
}
